import { useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParallaxCarousel } from './parallaxCarousel/ParallaxCarousel.vendor';
import SectionTitle from './SectionTitle';
import {
  type ParallaxCarouselPresentationVm,
} from '../viewModels/newHome.vm';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  HOME_PAGE_BANNER_CLICKED,
  HOME_PAGE_BANNER_VIEWED,
  bannerPositionFromSlideId,
  newHomeOfferBannerBaseEventProperties,
  newHomeOfferBannerClickEventProperties,
  resolveNewHomeOfferBannerDisplayNameFromSlide,
  type ParallaxBannerSlideFields,
} from '../analytics/homePageBannerCarousel';

/** Passed to `IntersectionObserver` (`useIntersectionObserver` `threshold`). Tune freely. */
const PARALLAX_BANNER_VIEWED_IO_THRESHOLDS: number[] = [0, 0.25, 0.5, 0.75, 1];

/** Carousel must be at least this visible (0–1) to count as in-view for banner viewed + autoplay gating. */
const PARALLAX_BANNER_VIEWED_MIN_INTERSECTION_RATIO = 1;

function parsePercent(value: string | number | undefined, fallback: number): number {
  if (value == null || value === '') return fallback;
  const normalized =
    typeof value === 'number' ? String(value) : value.trim().replace('%', '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveCarouselImageFromJson(
  item: ParallaxCarouselPresentationVm['items'][number],
  isDesktop: boolean,
  blankImageUrl: string,
): string {
  const desktopSrc = item.dImageUrl?.trim() || item.imageUrlDesktop?.trim();
  const mobileSrc = item.imageUrl?.trim();
  const resolved = (isDesktop ? desktopSrc || mobileSrc : mobileSrc || desktopSrc) || '';
  return resolved || blankImageUrl;
}

export default function ParallaxCarouselSection({
  data,
  sectionKey: _sectionKey,
  eventLabel,
}: {
  data: ParallaxCarouselPresentationVm;
  sectionKey: string;
  eventLabel: string;
}) {
  const isDesktop = useIsDesktop();
  const { analytics, isInitialized } = useAnalytics();
  const navigate = useNavigate();
  const showParallaxArrows = isDesktop || data.showParallaxArrowsOnMobile;

  const parallaxBannerFullyInViewRef = useRef(false);

  const onParallaxCarouselIntersection = useCallback((entry: IntersectionObserverEntry) => {
    parallaxBannerFullyInViewRef.current =
      entry.isIntersecting && entry.intersectionRatio >= PARALLAX_BANNER_VIEWED_MIN_INTERSECTION_RATIO;
  }, []);

  const { ref: parallaxCarouselViewportRef } = useIntersectionObserver<HTMLDivElement>(
    { threshold: PARALLAX_BANNER_VIEWED_IO_THRESHOLDS },
    onParallaxCarouselIntersection,
  );

  const slides = useMemo(
    () =>
      data.items.map((item) => ({
        id: item.position,
        image: resolveCarouselImageFromJson(item, isDesktop, data.blankImageUrl),
        title: item.title,
        categoryName: item.categoryName,
        tag: item.collection,
        alt: item.title,
        href: item.link,
        shouldRefresh: item.shouldRefresh ?? false,
      })),
    [data.blankImageUrl, data.items, isDesktop],
  );

  const onParallaxUserSlideChange = useCallback(
    (activeIdx: number) => {
      if (!parallaxBannerFullyInViewRef.current) return;
      if (!analytics || !isInitialized) return;
      const slide = slides[activeIdx];
      if (!slide) return;
      const bannerDisplayName = resolveNewHomeOfferBannerDisplayNameFromSlide(slide);
      try {
        analytics.trackEvent(
          HOME_PAGE_BANNER_VIEWED,
          newHomeOfferBannerBaseEventProperties({
            bannerDisplayName,
            bannerPosition: bannerPositionFromSlideId(slide.id),
          }),
        );
      } catch (error) {
        console.error('Error tracking Home Page Banner Viewed (parallax):', error);
      }
    },
    [analytics, isInitialized, slides],
  );

  const onParallaxBannerNavigate = useCallback(
    (slide: ParallaxBannerSlideFields) => {
      if (slide.href && analytics && isInitialized) {
        const bannerDisplayName = resolveNewHomeOfferBannerDisplayNameFromSlide(slide);
        const eventProperties = newHomeOfferBannerClickEventProperties({
          bannerDisplayName,
          bannerPosition: bannerPositionFromSlideId(slide.id),
          targetUrl: slide.href,
        });
        try {
          analytics.trackEvent(HOME_PAGE_BANNER_CLICKED, eventProperties);
        } catch (error) {
          console.error('Error tracking Home Page Banner Clicked (parallax):', error);
        }
      }

      const href = slide.href;
      if (!href) return true;
      if (slide.shouldRefresh || /goldcoin|build-your-own-diamond|\/tnc\.html/i.test(href)) {
        // Let the vendor carousel fall back to a real navigation (window.location / window.open).
        return false;
      }
      navigate(href);
      return true;
    },
    [analytics, isInitialized, navigate],
  );

  const peekLeftVw = useMemo(
    () =>
      parsePercent(
        isDesktop
          ? data.inactiveBasisLg ?? data.inactiveBasisSm ?? data.inactiveBasis
          : data.inactiveBasisSm ?? data.inactiveBasis,
        10,
      ),
    [data.inactiveBasis, data.inactiveBasisLg, data.inactiveBasisSm, isDesktop],
  );
  const peekRightVw = useMemo(() => {
    const nextResolved =
      isDesktop ? data.nextBasisLg ?? data.nextBasisSm ?? data.nextBasis : data.nextBasisSm ?? data.nextBasis;
    if (nextResolved != null && String(nextResolved).trim() !== '') {
      return parsePercent(nextResolved, 18);
    }
    return parsePercent(
      isDesktop ? data.inactiveBasisLg ?? data.inactiveBasisSm ?? data.inactiveBasis : data.inactiveBasisSm ?? data.inactiveBasis,
      18,
    );
  }, [
    data.inactiveBasis,
    data.inactiveBasisLg,
    data.inactiveBasisSm,
    data.nextBasis,
    data.nextBasisLg,
    data.nextBasisSm,
    isDesktop,
  ]);

  function getCardHeight() {
    if (!isDesktop) return '78.4vw';
    return window.innerWidth > 1440 ? '30vw' : '35vw';
  }

  return (
    <section
      style={{ backgroundColor: data.bgColor, color: data.textColor }}
      data-analytics-section-name={eventLabel}
      className="w-full pb-5"
    >
      {data.title && <SectionTitle title={data.title} />}

      <div ref={parallaxCarouselViewportRef} className="nh-offers-parallax-carousel w-full px-0 md:px-4">
        <style>{`
          .nh-offers-parallax-carousel .rpc-card,
          .nh-offers-parallax-carousel .rpc-card.rpc-card--active {
            box-shadow: none;
          }
          /* In-slide arrows (mobile / when not using viewport overlay). */
          .nh-offers-parallax-carousel .rpc-arrow {
            pointer-events: auto;
            background: rgba(255, 255, 255, 0.28);
            border: 1px solid rgba(255, 255, 255, 0.45);
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
            width: 38px;
            height: 38px;
          }
          /* Viewport-overlay arrows (desktop): match glass treatment, sit above track. */
          .nh-offers-parallax-carousel .rpc-viewport-arrow-btn {
            background: rgba(255, 255, 255, 0.28);
            border: 1px solid rgba(255, 255, 255, 0.45);
          }
          ${!showParallaxArrows ? `
          .nh-offers-parallax-carousel .rpc-arrow,
          .nh-offers-parallax-carousel .rpc-viewport-arrow-btn {
            display: none !important;
            pointer-events: none;
          }` : ''}
          .nh-offers-parallax-carousel .rpc-card--prev .rpc-arrow--left {
            left: calc(100% - 20px);
            transform: translate(-100%, -50%);
          }
          .nh-offers-parallax-carousel .rpc-card--next .rpc-arrow--right {
            left: 20px;
            transform: translate(0, -50%);
          }
        `}</style>
        <ParallaxCarousel
          key={slides.length}
          slides={slides}
          showHeader={false}
          peekLeftVw={peekLeftVw}
          peekRightVw={peekRightVw}
          cardHeight={getCardHeight()}
          gap={isDesktop ? '12px' : '8px'}
          borderRadius="16px"
          loop={true}
          showArrows={showParallaxArrows}
          autoPlay={data.slideInterval > 0}
          autoPlayDelay={data.slideInterval}
          linkTarget="_self"
          parallaxRange={data.parallaxClamp}
          parallaxLerp={data.parallaxFactor}
          arrowsViewportInsetPx={isDesktop ? 20 : undefined}
          onUserSlideChange={onParallaxUserSlideChange}
          onActiveSlideNavigate={onParallaxBannerNavigate}
        />
      </div>
    </section>
  );
}
